import { popupTargetFromElement } from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed, effect, signal } from '@preact/signals-core';
import { css } from 'lit';
import { property } from 'lit/decorators.js';

import { renderUniLit, trackSelectedAttachmentCell } from '../../../../core';
import type {
  CellRenderProps,
  DataViewCellLifeCycle,
} from '../../../../core/property';
import { DATA_VIEW_CELL_PRESENCE_EVENT } from '../../../../core/row-focus';
import {
  dataViewCellContainerStyles,
  shouldExpandDataViewCell,
} from '../../../../core/view/cell-container';
import {
  TableViewAreaSelection,
  TableViewRowSelection,
  type TableViewSelectionWithType,
} from '../../selection';
import type { TableSingleView } from '../../table-view-manager';
import type { VirtualTableViewUILogic } from '../table-view-ui-logic';
import type { TableGridCell } from '../types';
import { popRowMenu } from './menu';
import { rowSelectedBg } from './row-header-css';
import { applyVirtualTableCellSeparators } from './row-separator';
export class DatabaseCellContainer extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    ${dataViewCellContainerStyles('affine-database-virtual-cell-container', {
      rootBoxSizing: 'content-box',
      includeRootHeight: false,
    })}
  `;

  private readonly _cell = signal<DataViewCellLifeCycle>();

  cell$ = computed(() => {
    return this.view.cellGetOrCreate(this.rowId, this.columnId);
  });

  selectCurrentCell = (editing: boolean) => {
    if (this.view.readonly$.value) {
      return;
    }
    const selectionView = this.selectionView;
    if (selectionView) {
      const selection = selectionView.selection;
      if (selection && this.isSelected(selection) && editing) {
        selectionView.selection = TableViewAreaSelection.create({
          groupKey: this.groupKey,
          focus: {
            rowIndex: this.rowIndex$.value,
            columnIndex: this.columnIndex$.value,
          },
          isEditing: true,
        });
      } else {
        selectionView.selection = TableViewAreaSelection.create({
          groupKey: this.groupKey,
          focus: {
            rowIndex: this.rowIndex$.value,
            columnIndex: this.columnIndex$.value,
          },
          isEditing: false,
        });
      }
    }
  };

  get cell(): DataViewCellLifeCycle | undefined {
    return this._cell.value;
  }

  private get selectionView() {
    return this.tableViewLogic.selectionController;
  }

  get rowSelected$() {
    return this.gridCell.row.data.selected$;
  }

  contextMenu = (e: MouseEvent) => {
    if (this.view.readonly$.value) {
      return;
    }
    const selection = this.selectionView;
    if (!selection) {
      return;
    }
    e.preventDefault();
    const row = { id: this.rowId, groupKey: this.groupKey };
    if (!TableViewRowSelection.includes(selection.selection, row)) {
      selection.selection = TableViewRowSelection.create({
        rows: [row],
      });
    }
    popRowMenu(this.tableViewLogic, popupTargetFromElement(this), selection);
  };

  private readonly _stopKeyboardPropagationInEditing = (
    event: KeyboardEvent
  ) => {
    if (this.isEditing$.value && event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  private readonly enterEditingFromDoubleClick = (event: MouseEvent) => {
    if (this.isEditing$.value) return;
    if (this.view.readonly$.value) return;
    if (this.column$.value?.valueReadonly$.value) return;
    const selection = this.selectionView;
    if (!selection) return;
    event.stopPropagation();
    this.cell?.setEditingPoint?.({
      clientX: event.clientX,
      clientY: event.clientY,
    });
    selection.selection = TableViewAreaSelection.create({
      groupKey: this.groupKey,
      focus: {
        rowIndex: this.rowIndex$.value,
        columnIndex: this.columnIndex$.value,
      },
      isEditing: true,
    });
  };

  private syncCellFrameAttributes() {
    const frame = this.parentElement;
    if (!frame) {
      return;
    }
    frame.classList.add('database-cell-frame');
    frame.dataset.databaseCellFrame = 'true';
    frame.dataset.rowId = this.rowId;
    frame.dataset.columnId = this.columnId;
    frame.dataset.rowIndex = String(this.rowIndex$.value);
    frame.dataset.columnIndex = String(this.columnIndex$.value);
  }

  private publishCellPresenceTarget() {
    this.dispatchEvent(
      new CustomEvent(DATA_VIEW_CELL_PRESENCE_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          columnId: this.columnId,
          mode: this.isEditing$.value ? 'editing' : 'selected',
          rowId: this.rowId,
          viewId: this.view.id,
        },
      })
    );
  }

  override connectedCallback() {
    super.connectedCallback();
    this.dataset.rowId = this.rowId;
    this.dataset.columnId = this.columnId;
    this.disposables.add(
      effect(() => {
        this.dataset.rowIndex = String(this.rowIndex$.value);
        this.dataset.columnIndex = String(this.columnIndex$.value);
        this.syncCellFrameAttributes();
      })
    );
    this.disposables.addFromEvent(this, 'contextmenu', this.contextMenu);
    this.disposables.addFromEvent(
      this,
      'keydown',
      this._stopKeyboardPropagationInEditing
    );
    this.disposables.addFromEvent(
      this,
      'keyup',
      this._stopKeyboardPropagationInEditing
    );
    this.disposables.addFromEvent(this.parentElement, 'pointerdown', event => {
      if (!this.isEditing$.value) {
        this.selectionView?.handleCellPointerDown(event, this);
      }
    });
    this.disposables.addFromEvent(this.parentElement, 'click', event => {
      if (this.selectionView?.handleCellClick(event, this)) {
        return;
      }
      if (!this.isEditing$.value) {
        this.selectCurrentCell(!this.column$.value?.valueReadonly$.value);
      }
    });
    // Dblclick enters edit mode directly — see the non-virtual
    // table's counterpart for the rationale; the row-level click
    // handler in capture phase prevents the "2-click to edit" path
    // from working, so we offer an explicit dblclick shortcut that
    // matches the gantt swimlane label behaviour.
    this.disposables.addFromEvent(
      this,
      'dblclick',
      this.enterEditingFromDoubleClick
    );
    this.disposables.addFromEvent(
      this.parentElement,
      'dblclick',
      this.enterEditingFromDoubleClick
    );
    this.disposables.addFromEvent(this.parentElement, 'mouseenter', () => {
      this.gridCell.data.hover$.value = true;
    });
    this.disposables.addFromEvent(this.parentElement, 'mouseleave', () => {
      this.gridCell.data.hover$.value = false;
    });
    this.disposables.add(
      effect(() => {
        const rowSelected = this.rowSelected$.value;
        if (rowSelected) {
          this.parentElement?.classList.add(rowSelectedBg);
        } else {
          this.parentElement?.classList.remove(rowSelectedBg);
        }
      })
    );

    // Effect for cell display mode (expanded, collapsed, or smart)
    this.disposables.add(
      effect(() => {
        const tableView = this.view as TableSingleView;
        const mode = tableView.cellDisplayMode$.value;

        const selection = this.selectionView?.selection;
        const expanded = shouldExpandDataViewCell({
          isEditing: this.isEditing$.value,
          mode,
          rowSelected: this.isRowSelectedForExpand(selection),
        });
        this.dataset['expanded'] = `${expanded}`;
      })
    );

    this.disposables.add(
      effect(() => {
        const selection = this.selectionView?.selection;
        this.isEditing$.value;
        const selected = !!selection && this.isSelected(selection);
        this.dataset['databaseCellSelected'] = `${selected}`;
        trackSelectedAttachmentCell(this, {
          columnType: this.column$.value?.type$.value ?? '',
          selected,
        });
        if (!selected) {
          return;
        }
        this.publishCellPresenceTarget();
      })
    );

    applyVirtualTableCellSeparators(this.parentElement);
  }

  override disconnectedCallback() {
    if (this.isEditing$.peek() && this.gridCell.columnIndex$.peek() < 0) {
      // A deleted virtual column can remove this container before the
      // selection controller can resolve the old cell by index. Flip the
      // canonical editing signal while the renderer is still connected so
      // its beforeExitEditingMode hook runs, then release the row lock.
      this.isEditing$.value = false;
      this.view.lockRows(false);
    }
    super.disconnectedCallback();
  }

  /**
   * Check if this row is currently selected (for smart expand mode)
   */
  private isRowSelectedForExpand(
    selection: TableViewSelectionWithType | undefined
  ): boolean {
    if (!selection) return false;

    if (selection.selectionType === 'area') {
      // Area selection: check if focus row matches
      if (selection.groupKey !== this.groupKey) return false;
      return selection.focus.rowIndex === this.rowIndex$.value;
    } else if (selection.selectionType === 'row') {
      // Row selection: check if this row is in the selected rows
      return selection.rows.some(
        row => row.id === this.rowId && row.groupKey === this.groupKey
      );
    }
    return false;
  }

  isRowSelected$ = computed(() => {
    const selection = this.selectionView?.selection;
    if (selection?.selectionType !== 'row') {
      return false;
    }
    return selection.rows.some(row => row.id === this.rowId);
  });

  isSelected(selection: TableViewSelectionWithType) {
    if (selection.selectionType !== 'area') {
      return false;
    }
    if (selection.groupKey !== this.groupKey) {
      return false;
    }
    if (selection.focus.columnIndex !== this.columnIndex$.value) {
      return false;
    }
    return selection.focus.rowIndex === this.rowIndex$.value;
  }

  override render() {
    const renderer = this.column$.value?.renderer$.value;
    if (!renderer) {
      return;
    }
    const { view } = renderer;
    this.view.lockRows(this.isEditing$.value);
    this.dataset['editing'] = `${this.isEditing$.value}`;
    this.dataset['databaseCellEditing'] = `${this.isEditing$.value}`;
    this.dataset['rowId'] = this.rowId;
    this.dataset['columnId'] = this.columnId;
    this.dataset['columnName'] = this.column$.value?.name$.value ?? '';
    this.dataset['columnType'] = this.column$.value?.type$.value ?? '';
    const props: CellRenderProps = {
      cell: this.cell$.value,
      isEditing$: this.isEditing$,
      selectCurrentCell: this.selectCurrentCell,
    };

    return renderUniLit(view, props, {
      ref: this._cell,
      style: {
        display: 'contents',
      },
    });
  }
  isEditing$ = signal(false);

  rowIndex$ = computed(() => {
    return this.gridCell.rowIndex$.value;
  });

  columnIndex$ = computed(() => {
    return this.gridCell.columnIndex$.value - 1;
  });

  column$ = computed(() => {
    return this.tableViewLogic.layoutSnapshot$.value.dataColumns.find(
      column => column.id === this.columnId
    )?.property;
  });

  get rowId() {
    return this.gridCell.row.rowId;
  }

  get columnId() {
    return this.gridCell.columnId;
  }

  get groupKey() {
    return this.gridCell.row.group.groupId;
  }

  @property({ attribute: false })
  accessor gridCell!: TableGridCell;

  @property({ attribute: false })
  accessor tableViewLogic!: VirtualTableViewUILogic;

  get view() {
    return this.tableViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-database-virtual-cell-container': DatabaseCellContainer;
  }
}
